import React, { useState, useEffect, useRef, useMemo} from 'react';
import { WalletValue, walletAmount, useAuth } from '../context/AuthContext';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { History, Timer, Lock, RotateCw, ShieldCheck, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useChatLayout } from '../context/ChatLayoutContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');

// ==========================================
// YENİ: K, M, B FORMATLAYICILARI (ÇİFT YÖNLÜ)
// ==========================================
const parseAmount = (valStr) => {
  if (!valStr) return 0;
  // ⚠️ BİNLİK AYIRICI TEMİZLENMELİ. formatAmount() büyük sayıları
  // toLocaleString ile üretiyor ("1,457,545"); parseFloat virgülde durup
  // 1 döndürüyordu. MAX butonuna basan kullanıcı 1 birim bahis oynuyordu.
  let str = valStr.toString().trim().toLowerCase().replace(/[,\s]/g, '');
  let multiplier = 1;
  if (str.endsWith('k')) { multiplier = 1000; str = str.slice(0, -1); }
  else if (str.endsWith('m')) { multiplier = 1000000; str = str.slice(0, -1); }
  else if (str.endsWith('b')) { multiplier = 1000000000; str = str.slice(0, -1); }
  
  const parsedValue = parseFloat(str);
  return isNaN(parsedValue) ? 0 : Math.floor(parsedValue * multiplier);
};

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === 0) return '';
  if (amount >= 1000000) return amount.toLocaleString();
  if (amount >= 1000) return parseFloat((amount / 1000).toFixed(2)) + 'K';
  return amount.toString();
};

const ROULETTE_ORDER = [
  { type: 'green', number: 0 }, { type: 'red', number: 1 }, { type: 'black', number: 14 },
  { type: 'red', number: 2 }, { type: 'black', number: 13 }, { type: 'red', number: 3 },
  { type: 'black', number: 12 }, { type: 'red', number: 4 }, { type: 'black', number: 11 },
  { type: 'red', number: 5 }, { type: 'black', number: 10 }, { type: 'red', number: 6 },
  { type: 'black', number: 9 }, { type: 'red', number: 7 }, { type: 'black', number: 8 },
];

// ⚠️ BİLEŞEN DIŞINDA: `wheelSlices` useMemo'su render sırasında bunu
// çağırıyor. Bileşen içinde `const` olarak dururken, useMemo ondan ÖNCE
// geldiği için TDZ hatası veriyordu ("Cannot access before initialization").
// Saf fonksiyon olduğu için dışarı almak ayrıca her render'da yeniden
// oluşturulmasını da engelliyor.
const getColorClass = (type) => {
  if (type === 'red') return 'bg-rose-500 shadow-rose-500/50';
  if (type === 'black') return 'bg-zinc-800 shadow-black/50 border border-zinc-700';
  return 'bg-[#16A34A] shadow-[#16A34A]/50';
};

const generateWheel = (repeats = 15) => {
  let wheel = [];
  for (let i = 0; i < repeats; i++) wheel = [...wheel, ...ROULETTE_ORDER];
  return wheel;
};

export const Roulette = () => {
  const { user, token, isAuthenticated, balance, fmtMoney, wallet, betToRaw, rawToBet, betLimits } = useAuth();
  const [betAmount, setBetAmount] = useState('');
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  
  const [phase, setPhase] = useState('betting'); 
  const [timeRemaining, setTimeRemaining] = useState(15);
  
  const [history, setHistory] = useState([]); 
  const [allBets, setAllBets] = useState([]); 
  
  const [serverHash, setServerHash] = useState(''); 
  const [lastWinner, setLastWinner] = useState(null); 
  
  const [wheelItems] = useState(() => generateWheel(15));
  const controls = useAnimation();
  const itemWidth = 80;
  // ⚡ 225 dilim HİÇ DEĞİŞMİYOR ama her WS mesajında (saniyelik sayaç,
  // her bahis) yeniden oluşturuluyordu. Bir kez üretilip saklanır.
  // ⚠️ itemWidth'ten SONRA tanımlanmalı: yukarıda olsaydı TDZ hatası.
  const wheelSlices = useMemo(() => wheelItems.map((item, i) => (
    <div key={i} style={{ width: itemWidth }} className="flex-shrink-0 h-16 md:h-20 px-1">
      <div className={`w-full h-full rounded-lg flex items-center justify-center shadow-lg ${getColorClass(item.type)}`}>
        <span className="font-heading font-bold text-xl md:text-2xl text-white/90 drop-shadow-md">
          {item.number}
        </span>
      </div>
    </div>
  )), [wheelItems, itemWidth]);
  const wsRef = useRef(null); 
  const wheelContainerRef = useRef(null);
  const tickSound = useRef(null);

  // ⚡ SES HAVUZU: 6 öğe bir kez yaratılır ve sırayla yeniden kullanılır.
  // Eskiden her tıkta cloneNode() ile YENİ öğe yaratılıyordu (5 saniyelik
  // dönüşte 150+ adet) ve sayfa gözle görülür şekilde kasıyordu.
  const tickPool = useRef([]);
  const tickIdx = useRef(0);
  const lastTickAt = useRef(0);

  useEffect(() => {
    if (typeof Audio === "undefined") return;
    const pool = [];
    for (let i = 0; i < 6; i++) {
      const a = new Audio('/reel_tick.mp3');
      a.preload = 'auto';
      a.volume = 0.15;
      pool.push(a);
    }
    tickPool.current = pool;
    tickSound.current = pool[0];
    return () => { pool.forEach(a => { try { a.pause(); a.src = ''; } catch (e) {} }); };
  }, []);

  useEffect(() => {
    let reconnectInterval; 

    const connectWebSocket = () => {
      const wsToken = token || 'guest';
      const ws = new WebSocket(`${WS_URL}/ws/roulette/${wsToken}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("🟢 Telsiz bağlantısı kuruldu (WebSocket Open)");
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'ROULETTE_STATE') {
            setPhase(prevPhase => {
              if (data.phase === 'betting' && prevPhase !== 'betting') {
                setAllBets([]); 
                setLastWinner(null);
                controls.set({ x: 0 }); 
              }
              return data.phase;
            });
            
            setTimeRemaining(data.time);
            if (data.server_seed_hash) setServerHash(data.server_seed_hash);
            
            if (data.history) {
                setHistory(data.history.map((color, i) => ({ id: `hist-init-${i}-${Date.now()}`, color })));
            }
            if (data.bets) setAllBets(data.bets);
          }
          else if (data.type === 'NEW_BET') {
            setAllBets(prev => [...prev, data.bet]);
          }
          else if (data.type === 'ROULETTE_RESULT') {
            setPhase('spinning');
            handleServerSpin(data.winningNumber, data.color, data.server_seed);
          }
          else if (data.type === 'ERROR') {
            toast.error(data.message);
          }
        } catch (error) { console.error("WS Parse Error:", error); }
      };

      ws.onclose = () => {
        console.warn("🔴 Bağlantı koptu! 2 saniye içinde tekrar deneniyor...");
        reconnectInterval = setTimeout(connectWebSocket, 2000);
      };

      ws.onerror = (error) => {
        console.error("WebSocket Hatası:", error);
        ws.close(); 
      };
    };

    connectWebSocket();

    return () => {
      clearTimeout(reconnectInterval);
      if (wsRef.current) {
        wsRef.current.onclose = null; 
        wsRef.current.close();
      }
    };
  }, [token, controls]);

  const handleServerSpin = async (winningNumber, winningColor, serverSeed) => {
    let lastTickIndex = 0;
    const targetIndex = (ROULETTE_ORDER.length * 10) + ROULETTE_ORDER.findIndex(item => item.number === winningNumber);
    const randomOffset = Math.floor(Math.random() * 40) - 20; 
    const containerWidth = wheelContainerRef.current ? wheelContainerRef.current.offsetWidth : 800; 
    const centerOffset = containerWidth / 2;
    const finalPosition = -(targetIndex * itemWidth) + centerOffset - (itemWidth / 2) + randomOffset;

    await controls.start({
      x: finalPosition,
      transition: { 
        duration: 5, 
        ease: [0.15, 0.85, 0.1, 1],
        onUpdate: (latest) => {
          const currentIndex = Math.floor(Math.abs(latest) / itemWidth);
          if (currentIndex === lastTickIndex) return;
          lastTickIndex = currentIndex;

          // ⚠️ Dönüşün başında dilimler saniyede onlarca kez geçiyor;
          // 40 ms'den sık tıklar hem duyulmuyor hem de ses kanallarını
          // tıkıyor. Atlamak sesi bozmaz, yükü ciddi biçimde düşürür.
          const now = performance.now();
          if (now - lastTickAt.current < 40) return;
          lastTickAt.current = now;

          const pool = tickPool.current;
          if (!pool.length) return;
          const a = pool[tickIdx.current];
          tickIdx.current = (tickIdx.current + 1) % pool.length;
          try { a.currentTime = 0; a.play().catch(() => {}); } catch (e) {}
        }
      } 
    });

    setLastWinner({ number: winningNumber, color: winningColor, seed: serverSeed });
    setHistory(prev => [{ id: `hist-win-${Date.now()}`, color: winningColor }, ...prev].slice(0, 10)); 
  };

  const placeBet = (color) => {
    if (!isAuthenticated) return toast.error("Please log in first to place a bet!");

    // 🎯 Kullanıcı AKTİF CÜZDANIN biriminde yazar (Robux → site birimi,
    // BB Coin → BB). betToRaw saklama birimine çevirir: BB'de ×1000.
    // Bu satır olmadan "10" yazan oyuncu 10 mili-BB (0,01 BB) oynardı.
    const amount = betToRaw(parseAmount(betAmount)); // YENİ: Harfli stringi tam sayıya çevirir
    
    if (!amount || amount <= 0) return toast.error("Invalid amount!");
    // ⚠️ Limitler cüzdana göre; "1 – 100.000" SİTE birimidir ve
    // BB Coin bahislerini haksız yere reddederdi.
    if (!amount || amount < betLimits.min_bet || amount > betLimits.max_bet) {
      return toast.error(`Bet must be between ${walletAmount(betLimits.min_bet, wallet)} and ${walletAmount(betLimits.max_bet, wallet)}.`);
    }
    // 🍀 AKTİF CÜZDANIN bakiyesiyle karşılaştır (LC'de mili-LC).
    if (amount > balance) return toast.error("Insufficient balance!");
    if (phase !== 'betting') return toast.error("Bets are currently closed!");
    
    if (allBets.some(b => b.user_id === user?.id && b.color === color)) {
       return toast.info("You have already placed a bet on this color!");
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'PLACE_BET', color: color, amount: amount }));
    } else {
      toast.error("Server connection lost!");
    }
  };

  // YENİ: K, M, B Destekli Hızlı Buton Fonksiyonları
  const handleBetChange = (val) => setBetAmount(val);
  
  const multiplyBet = (multiplier) => {
    const current = parseAmount(betAmount);
    setBetAmount(String(Math.floor(current * multiplier)));
  };

  const addBetAmount = (val) => {
    const current = parseAmount(betAmount);
    setBetAmount(String(current + val));
  };

  const renderBets = (colorType) => {
    const colorBets = allBets
        .filter(b => b.color === colorType)
        .sort((a, b) => b.amount - a.amount);
    
    const totalAmount = colorBets.reduce((sum, bet) => sum + bet.amount, 0);
    const myBet = colorBets.find((b) => b.user_id === user?.id) || null;

    return (
      <>
        {/* ── MOBİL ÖZET (< sm) ────────────────────────────────────────────
            ⚠️ Mobilde sütun ~105px; oyuncu adı + tutar satırı okunamaz hale
               gelir ve 200px'lik liste sayfayı gereksiz uzatır. Bahis
               yapmak için gereken tek bilgi "kaç kişi, ne kadar" — onu
               kompakt gösterip listeyi geniş ekrana bırakıyoruz. */}
        <div className="sm:hidden mt-2 bg-[#15171a] border border-[#2A2D31] rounded-lg px-2 py-1.5 flex items-center justify-between">
          <span className="flex items-center gap-1 text-zinc-400 text-[10px]">
            <Users className="w-3 h-3" />
            {colorBets.length}
          </span>
          <span className="font-bold text-white text-[11px] truncate ml-1">
            <WalletValue amount={totalAmount} wallet={wallet} />
          </span>
        </div>
        {/* Kendi bahsin mobilde de görünsün: masaya girip girmediğini bilmek
            tur kapanmadan önce en çok merak edilen şey. */}
        {myBet && (
          <div className="sm:hidden mt-1 rounded-lg bg-[#B059E8]/20 border border-[#B059E8]/30 px-2 py-1 text-center">
            <span className="text-[10px] font-bold text-[#c98bf0]">
              You: <WalletValue amount={myBet.amount} wallet={myBet.wallet} />
            </span>
          </div>
        )}

      <div className="hidden sm:flex mt-3 bg-[#15171a] border border-[#2A2D31] rounded-xl overflow-hidden flex-col h-[200px] lg:h-[220px]">
        <div className="bg-[#2A2D31]/30 px-3 py-2 border-b border-[#2A2D31] flex justify-between items-center shrink-0">
            <div className="flex items-center gap-1.5 text-zinc-400 text-xs">
                <Users className="w-3.5 h-3.5" />
                <span>{colorBets.length} Players</span>
            </div>
            {/* YENİ: Kutudaki Toplam Parayı Formatla (Örn: 2.5K $) */}
            <span className="font-bold text-white text-sm"><WalletValue amount={totalAmount} wallet={wallet} /></span>
        </div>

        <div className="overflow-y-auto p-1.5 space-y-1 flex-1 scrollbar-thin scrollbar-thumb-zinc-700">
            {colorBets.length === 0 ? (
                <div className="h-full flex items-center justify-center text-zinc-600 text-xs font-body">
                    No bets yet
                </div>
            ) : (
                colorBets.map((bet, i) => (
                    <div key={i} className={`flex justify-between items-center px-2 py-1.5 rounded-lg text-xs transition-all ${bet.user_id === user?.id ? 'bg-[#B059E8]/20 border border-[#B059E8]/30' : 'bg-[#1A1D20] hover:bg-[#33363b]/50'}`}>
                        <span className={`truncate max-w-[100px] font-medium ${bet.user_id === user?.id ? 'text-[#c98bf0]' : 'text-zinc-300'}`}>
                            {bet.username}
                        </span>
                        <span className="text-white font-bold">
                            {/* YENİ: Tek tek oyuncu bahislerini de formatla */}
                            {/* ⚠️ Masa karışık cüzdanlı olabilir; her bahis KENDİ mührüyle. */}
                            <WalletValue amount={bet.amount} wallet={bet.wallet} />
                        </span>
                    </div>
                ))
            )}
        </div>
      </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#0E0F11]" data-testid="roulette-page">
      <div className="max-w-[1800px] mx-auto px-4 pt-24 pb-8">
        <div className={`flex flex-col lg:flex-row gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
          <main className="flex-1 min-w-0 relative">
            <div className="absolute top-10 left-1/4 w-96 h-96 bg-[#B059E8]/5 rounded-full blur-[120px] pointer-events-none" />

            <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-4 mb-4 flex flex-col md:flex-row items-center justify-between shadow-lg">
              <div className="flex items-center gap-4 mb-3 md:mb-0">
                {phase === 'betting' && (
                  <div className="flex items-center gap-2 text-[#2ecf6e]">
                    <Timer className="w-5 h-5 animate-pulse" />
                    <span className="font-heading font-bold text-base md:text-lg uppercase tracking-wider">Betting Open</span>
                  </div>
                )}
                {phase === 'locked' && (
                  <div className="flex items-center gap-2 text-rose-400">
                    <Lock className="w-5 h-5" />
                    <span className="font-heading font-bold text-base md:text-lg uppercase tracking-wider">Bets Locked</span>
                  </div>
                )}
                {phase === 'spinning' && (
                  <div className="flex items-center gap-2 text-[#c98bf0]">
                    <RotateCw className="w-5 h-5 animate-spin" />
                    <span className="font-heading font-bold text-base md:text-lg uppercase tracking-wider">Wheel Spinning</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                {serverHash && (
                  <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-[#15171a] rounded-lg border border-[#B059E8]/20 text-xs text-zinc-500 mm-num">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#16A34A]" />
                    <span className="truncate w-32" title={serverHash}>Hash: {serverHash.substring(0, 12)}...</span>
                  </div>
                )}
                {(phase === 'betting' || phase === 'locked') && (
                  <div className="bg-[#15171a] px-5 py-1.5 rounded-xl border border-[#2A2D31]">
                    <span className={`mm-num text-xl font-bold ${phase === 'locked' ? 'text-rose-400' : 'text-white'}`}>
                      {timeRemaining}s
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3 px-2 overflow-hidden w-full [mask-image:linear-gradient(to_right,white_80%,transparent_100%)] pb-2">
              <History className="w-4 h-4 text-zinc-500 mr-2 flex-shrink-0" />
              <div className="flex items-center gap-2 w-full">
                 <AnimatePresence>
                   {history.length === 0 ? (
                     <span className="text-zinc-600 text-xs">Waiting for first round...</span>
                   ) : (
                     history.map((item) => (
                       <motion.div 
                         layout 
                         key={item.id}
                         initial={{ opacity: 0, x: -20, scale: 0.5 }}
                         animate={{ opacity: 1, x: 0, scale: 1 }}
                         exit={{ opacity: 0, scale: 0.5 }}
                         className={`min-w-[28px] h-6 rounded-full flex items-center justify-center shadow-lg flex-shrink-0 ${getColorClass(item.color)}`}
                       />
                     ))
                   )}
                 </AnimatePresence>
              </div>
            </div>

            <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-4 md:p-5 mb-4 relative shadow-2xl flex flex-col">
              
              <AnimatePresence>
                 {phase === 'spinning' && lastWinner && (
                    <motion.div
                       initial={{ opacity: 0, y: -10, scale: 0.8 }}
                       animate={{ opacity: 1, y: 0, scale: 1 }}
                       exit={{ opacity: 0, scale: 0.8 }}
                       className="absolute top-1 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center pointer-events-none"
                    >
                       <div className={`px-4 py-1.5 rounded-xl font-heading font-bold text-xl md:text-2xl shadow-xl border-2 whitespace-nowrap
                           ${lastWinner.color === 'red' ? 'bg-rose-500 text-white border-rose-400 shadow-[0_0_20px_rgba(244,63,94,0.5)]' :
                             lastWinner.color === 'black' ? 'bg-zinc-800 text-white border-zinc-600 shadow-[0_0_20px_rgba(0,0,0,0.5)]' :
                             'bg-[#16A34A] text-white border-[#2ecf6e] shadow-[0_0_20px_rgba(22,163,74,0.5)]'}
                       `}>
                           {lastWinner.number}
                       </div>
                       <div className={`w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent mt-1
                           ${lastWinner.color === 'red' ? 'border-t-rose-500' : lastWinner.color === 'black' ? 'border-t-zinc-800' : 'border-t-[#16A34A]'}
                       `}></div>
                    </motion.div>
                 )}
              </AnimatePresence>

              {/* ÇARK */}
              <div ref={wheelContainerRef} className="relative h-20 md:h-24 overflow-hidden rounded-xl bg-[#15171a] shadow-inner mt-4 before:absolute before:inset-y-0 before:left-0 before:w-8 md:before:w-16 before:bg-gradient-to-r before:from-[#1A1D20] before:to-transparent before:z-10 after:absolute after:inset-y-0 after:right-0 after:w-8 md:after:w-16 after:bg-gradient-to-l after:from-[#1A1D20] after:to-transparent after:z-10">
                <div className="absolute top-0 bottom-0 left-1/2 w-1 bg-white z-20 -translate-x-1/2 shadow-[0_0_15px_rgba(255,255,255,0.8)] pointer-events-none" />
                
                <motion.div 
                  className="flex h-full items-center absolute left-0"
                  animate={controls}
                  initial={{ x: 0 }}
                >
                  {wheelSlices}
                </motion.div>
              </div>
            </div>

            <div className={`bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-4 md:p-5 transition-opacity duration-300 ${phase !== 'betting' ? 'opacity-50 grayscale-[50%]' : ''}`}>
              <div className="flex flex-col md:flex-row gap-3 mb-4">
                <div className="flex-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-zinc-400 font-bold text-sm">$</span>
                  </div>
                  <input
                    type="text" // YENİ: type="number" kaldırıldı ki harf yazılabilsin
                    value={betAmount}
                    onChange={(e) => handleBetChange(e.target.value)}
                    placeholder="1"
                    disabled={phase !== 'betting'}
                    className="w-full bg-[#15171a] border border-[#2A2D31] rounded-xl py-2.5 pl-9 pr-4 text-white font-bold outline-none focus:border-[#B059E8] transition-colors disabled:cursor-not-allowed uppercase"
                  />
                </div>

                {/* ⚠️ Mobilde 5 buton 360px'e sığıyor ama dokunma hedefleri
                    ~45px kalıyordu (Apple/Google önerisi 44px taban, ama
                    yükseklik de gerekiyor). flex-1 ile eşit paylaşıp
                    yüksekliği artırdık; geniş ekranda eski görünüm korunur. */}
                <div className="flex gap-1.5">
                  <button onClick={() => addBetAmount(10)} disabled={phase !== 'betting'} className="flex-1 md:flex-none px-2 md:px-3 py-2.5 md:py-1.5 bg-[#2A2D31]/50 hover:bg-[#33363b] active:bg-[#33363b] text-zinc-300 rounded-xl font-bold text-xs transition-colors disabled:cursor-not-allowed">+10</button>
                  <button onClick={() => setBetAmount('')} disabled={phase !== 'betting'} className="flex-1 md:flex-none px-2 md:px-3 py-2.5 md:py-1.5 bg-[#2A2D31]/50 hover:bg-[#33363b] active:bg-[#33363b] text-zinc-300 rounded-xl font-bold text-xs transition-colors disabled:cursor-not-allowed">Clear</button>
                  <button onClick={() => multiplyBet(0.5)} disabled={phase !== 'betting'} className="flex-1 md:flex-none px-2 md:px-3 py-2.5 md:py-1.5 bg-[#2A2D31]/50 hover:bg-[#33363b] active:bg-[#33363b] text-zinc-300 rounded-xl font-bold text-xs transition-colors disabled:cursor-not-allowed">1/2</button>
                  <button onClick={() => multiplyBet(2)} disabled={phase !== 'betting'} className="flex-1 md:flex-none px-2 md:px-3 py-2.5 md:py-1.5 bg-[#2A2D31]/50 hover:bg-[#33363b] active:bg-[#33363b] text-zinc-300 rounded-xl font-bold text-xs transition-colors disabled:cursor-not-allowed">x2</button>
                  <button onClick={() => setBetAmount(String(rawToBet(balance || 0)))} disabled={phase !== 'betting'} className="flex-1 md:flex-none px-2 md:px-3 py-2.5 md:py-1.5 bg-[#B059E8]/20 hover:bg-[#B059E8]/40 active:bg-[#B059E8]/40 text-[#c98bf0] rounded-xl font-bold text-xs transition-colors border border-[#B059E8]/30 disabled:cursor-not-allowed">MAX</button>
                </div>
              </div>

              {/* ⚠️ MOBİLDE DE 3 SÜTUN. Önceden `grid-cols-1` idi: üç renk alt
                  alta diziliyor, her birinin altındaki 200px'lik bahis
                  listesiyle birlikte sayfa ~1200px'e uzuyordu. Kullanıcı
                  Black'e bahis yapmak için üç ekran kaydırmak zorundaydı —
                  ve tur 20 saniyede kapanıyor. Rulet masası doğası gereği
                  yan yana okunur. */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6">
                {['red', 'green', 'black'].map((colorType) => {
                  const isBetPlaced = allBets.some(b => b.user_id === user?.id && b.color === colorType);
                  
                  return (
                    <div key={colorType} className="flex flex-col">
                      <button 
                        onClick={() => placeBet(colorType)}
                        disabled={phase !== 'betting'}
                        className={`relative overflow-hidden group rounded-xl py-3 md:py-4 transition-all shadow-lg disabled:cursor-not-allowed border active:scale-[0.97]
                          ${colorType === 'red' ? 'bg-gradient-to-b from-rose-500 to-rose-700 hover:from-rose-400 hover:to-rose-600 shadow-rose-500/30 border-rose-600' : ''}
                          ${colorType === 'green' ? 'bg-gradient-to-b from-[#16A34A] to-[#15913f] hover:from-[#2ecf6e] hover:to-[#16A34A] shadow-[#16A34A]/30 border-[#16A34A]' : ''}
                          ${colorType === 'black' ? 'bg-gradient-to-b from-zinc-700 to-zinc-900 hover:from-zinc-600 hover:to-zinc-800 shadow-black/50 border-zinc-600' : ''}
                          ${isBetPlaced ? 'ring-2 ring-white scale-[0.98]' : ''}
                        `}
                      >
                        <div className="relative z-10 flex flex-col items-center">
                          {/* 360px ekranda sütun ~105px: başlık küçülmezse taşar. */}
                          <span className="text-white font-heading font-bold text-sm sm:text-xl md:text-2xl uppercase tracking-wide sm:tracking-wider mb-0.5">
                            {colorType === 'red' ? 'Red' : colorType === 'green' ? 'Green' : 'Black'}
                          </span>
                          <span className={`text-[10px] sm:text-xs font-bold ${colorType === 'red' ? 'text-rose-200' : colorType === 'green' ? 'text-[#86efac]' : 'text-zinc-400'}`}>
                            x{colorType === 'green' ? '14' : '2'}
                          </span>
                        </div>
                      </button>
                      
                      {renderBets(colorType)}
                    </div>
                  );
                })}
              </div>
            </div>

          </main>
        </div>
      </div>
    </div>
  );
};